import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import AvatarUpload from '../common/AvatarUpload';
import Input from '../common/Input';
import Button from '../common/Button';
import Loading from '../common/Loading';

const Settings = () => {
  const { userProfile, updateUserProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [formData, setFormData] = useState({
    displayName: userProfile?.displayName || '',
    email: userProfile?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [userSettings, setUserSettings] = useState({
    emailNotifications: false,
    inAppNotifications: true,
    desktopNotifications: false,
    emailFrequency: 'instant'
  });

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!userProfile) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', userProfile.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.settings) {
            setUserSettings(prev => ({ ...prev, ...userData.settings }));
          }
        }
      } catch (error) {
        console.error('Error fetching user settings:', error);
        notify.error('Failed to load settings');
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchUserSettings();
  }, [userProfile, notify]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.displayName.trim()) {
      notify.error('Display name is required');
      return;
    }

    try {
      setLoading(true);
      await updateUserProfile({
        displayName: formData.displayName.trim()
      });
      notify.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      notify.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsUpdate = async (key, value) => {
    if (!userProfile) return;

    try {
      setSaving(true);
      
      const newSettings = { ...userSettings, [key]: value };
      setUserSettings(newSettings);

      await updateDoc(doc(db, 'users', userProfile.uid), {
        settings: newSettings,
        updatedAt: new Date().toISOString()
      });

      notify.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      notify.error('Failed to update settings');
      // Revert on error
      setUserSettings(prev => ({ ...prev, [key]: !value }));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpdate = (avatarUrl) => {
    console.log('Avatar updated:', avatarUrl);
  };

  const requestDesktopNotifications = async () => {
    if (!('Notification' in window)) {
      notify.error('This browser does not support desktop notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      handleSettingsUpdate('desktopNotifications', true);
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        handleSettingsUpdate('desktopNotifications', true);
        notify.success('Desktop notifications enabled');
      } else {
        notify.error('Desktop notifications blocked');
      }
    }
  };

  if (settingsLoading) {
    return <Loading text="Loading settings..." />;
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Profile Settings */}
        <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Profile Settings
            </h2>
            
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <AvatarUpload
                  userId={userProfile?.uid}
                  currentAvatarUrl={userProfile?.avatarUrl}
                  displayName={userProfile?.displayName}
                  onAvatarUpdate={handleAvatarUpdate}
                  size="large"
                  showRemoveButton={true} 
                />
                
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {userProfile?.displayName}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {userProfile?.email}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Member since {new Date(userProfile?.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Display Name"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your display name"
                  required
                />
                
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled
                  className="opacity-50 cursor-not-allowed"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  className="w-full sm:w-auto"
                >
                  Update Profile
                </Button>
              </div>
            </form>
          </div>

          {/* Password Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Change Password
            </h2>
            
            <form className="space-y-4">
              <Input
                label="Current Password"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
              />
              
              <Input
                label="New Password"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
              />
              
              <Input
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
              />

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  disabled
                  className="w-full sm:w-auto"
                >
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-4 lg:space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preferences
            </h2>
            
            <div className="space-y-4">
              {/* Theme Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Dark Mode
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Switch between light and dark themes
                  </p>
                </div>
                <button
                  onClick={toggleTheme}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  role="switch"
                  aria-checked={theme === 'dark'}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      theme === 'dark' ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Email Notifications */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Email Notifications
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Receive email updates
                  </p>
                  
                  {userSettings.emailNotifications && (
                    <div className="mt-2">
                      <select
                        value={userSettings.emailFrequency}
                        onChange={(e) => handleSettingsUpdate('emailFrequency', e.target.value)}
                        disabled={saving}
                        className="block w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800"
                      >
                        <option value="instant">Instant</option>
                        <option value="daily">Daily Digest</option>
                        <option value="weekly">Weekly Digest</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={userSettings.emailNotifications}
                    onChange={(e) => handleSettingsUpdate('emailNotifications', e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              {/* In-App Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    In-App Notifications
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Show notification toasts
                  </p>
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={userSettings.inAppNotifications}
                    onChange={(e) => handleSettingsUpdate('inAppNotifications', e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              {/* Desktop Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Desktop Notifications
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Browser notifications
                  </p>
                </div>
                <div className="ml-3">
                  <input
                    type="checkbox"
                    checked={userSettings.desktopNotifications}
                    onChange={requestDesktopNotifications}
                    disabled={saving || Notification?.permission === 'denied'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Email Features Info */}
            {userSettings.emailNotifications && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Email notifications include:</strong>
                </p>
                <ul className="text-xs text-blue-600 dark:text-blue-400 mt-1 list-disc list-inside">
                  <li>Issue assignments</li>
                  <li>Status changes</li>
                  <li>New comments</li>
                </ul>
              </div>
            )}
          </div>

          {/* Account Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Account Information
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">User ID:</span>
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {userProfile?.uid?.substring(0, 8)}...
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Role:</span>
                <span className="text-sm text-gray-900 dark:text-white capitalize">
                  {userProfile?.role || 'user'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Member since:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(userProfile?.createdAt).toLocaleDateString()}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Last updated:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(userProfile?.updatedAt || userProfile?.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;